/**
 * Every word painted on the level, and the two machines that throw one: the
 * road signs, the notes on the tables, the monitor pictures, the hoses, and
 * the projector aimed at the valkokangas.
 *
 * None of it is hashed. A sign is a picture on a plane; move it, re-word it or
 * take it away and every leaderboard time stands.
 */

import { type ProjectionSpec, type SignSpec } from '../world/Level';
import { PLAYER_ALIAS } from './player';
import { CABLE_X, TOLPPA_Z } from './levelAnsalanka';
import { BAARI } from './levelBaari';
import { PROJEKTORI, TABLE_CENTERS, TEAM_NAMES, TEAM_SIGN, VALKOKANGAS } from './levelJuhlasali';
import {
  BED,
  KIMMON_YOPOYTA_X,
  KIMMO_BED_X,
  LAPPU,
  MATTRESS_TOP,
  NIMILAPPU,
  NUKKUJAN_YOPOYTA_X,
  PLAKAATTI,
  POKAALI_TOP,
  ROOMMATE_BED_X,
  ROOMMATE_NAME,
  YOPOYTA,
  YOPOYTA_Z,
} from './levelMakuuhuone';
import { ETEINEN, RISTEYS, STALLS } from './levelNavetanOvi';
import { ETELASEINA } from './levelPirtti';
import { TORNI } from './levelPorukka';
import { SAUNAN_OVI, VALVOJA } from './levelSaliVaki';

/* ================================================================= signs == */

export const signs: SignSpec[] = [
  { text: 'EINON PIRTTI', position: { x: 5, y: 2.5, z: 3.9 }, rotationY: Math.PI, width: 4.6, height: 0.8 },
  // Taped to the wall over the head of each bed, facing into the room: whose
  // bed is whose, in a pirtti that is rented by the mattress. Both are stuck to
  // the wall their own bed is flush against, so each faces the other across the
  // floor the player wakes up on. See `NIMILAPPU`.
  {
    text: PLAYER_ALIAS,
    position: {
      x: KIMMO_BED_X + NIMILAPPU.offWall,
      y: NIMILAPPU.y,
      z: BED.headZ + NIMILAPPU.alongBed,
    },
    rotationY: Math.PI / 2,
    width: NIMILAPPU.width,
    height: NIMILAPPU.height,
  },
  {
    text: ROOMMATE_NAME,
    position: {
      x: ROOMMATE_BED_X + BED.width - NIMILAPPU.offWall,
      y: NIMILAPPU.y,
      z: BED.headZ + NIMILAPPU.alongBed,
    },
    rotationY: -Math.PI / 2,
    width: NIMILAPPU.width,
    height: NIMILAPPU.height,
  },
  // Hung on the south wall over Kimmo's yöpöytä, so the row of cups under it
  // is a row of *something* rather than four anonymous lumps of brass. Same
  // wall as the note across the room and the same facing, one table over.
  {
    text: 'LANIJATKOJEN\nMESTARUUS',
    position: {
      x: KIMMON_YOPOYTA_X + YOPOYTA.width / 2,
      // Clear of the tallest rim by a hand's width: a plaque resting on the
      // cups it names would read as a shelf that had fallen on them.
      y: POKAALI_TOP + 0.05 + PLAKAATTI.height / 2,
      z: ETELASEINA.inner + 0.01,
    },
    rotationY: 0,
    width: PLAKAATTI.width,
    height: PLAKAATTI.height,
    // Engraved brass on stained wood, i.e. the two things the cups under it
    // are made of.
    color: '#e8cf92',
    background: '#2a1d12',
  },
  // Stood on his yöpöytä against the south wall, facing the room. Two lines,
  // because a note is written in lines and one 19 characters long on a strip
  // this wide would be under six centimetres tall - see `createSign`, which
  // sizes the font off the height and splits on `\n` for exactly this.
  {
    text: 'Hyvä yri,\nlimupoika',
    position: {
      x: NUKKUJAN_YOPOYTA_X + YOPOYTA.width / 2,
      y: MATTRESS_TOP + LAPPU.height / 2,
      z: YOPOYTA_Z.back + 0.03,
    },
    rotationY: 0,
    width: LAPPU.width,
    height: LAPPU.height,
    color: '#2b2620',
    background: '#efe6cf',
  },
  { text: 'EINON PIRTTI', position: { x: 33.45, y: 3.35, z: 1.75 }, rotationY: Math.PI / 2, width: 4.2, height: 0.8 },
  // Both of these hang under an eave rather than on a gable, so the board has
  // to finish below the roof slab: `aitat-katto` starts at the top of the wall
  // and oversails it by 0.4 m, so anything above 2.8 m here is inside the roof
  // and simply not there. Sat 5 cm under the eave, which is where a name board
  // gets nailed anyway.
  { text: 'AITAT', position: { x: 50, y: 2.35, z: 7.9 }, rotationY: Math.PI, width: 3.0, height: 0.8 },
  // On the gate, facing the back field - i.e. facing anybody who has come out
  // of the window and is looking for the way on. It is the only thing on the
  // level that says out loud what the fence behind it means.
  {
    text: 'ÄLÄ JÄTÄ PORTTIA AUKI',
    position: { x: 31.0, y: 1.55, z: -18.83 },
    rotationY: 0,
    width: 3.2,
    height: 0.36,
    color: '#101010',
    background: '#e6dfc9',
  },
  { text: 'PELTOLA', position: { x: 49, y: 5.4, z: -20.9 }, rotationY: 0, width: 4.4, height: 1.0 },
  { text: 'VAARIN PIRTTI', position: { x: 76, y: 3.6, z: 5.9 }, rotationY: Math.PI, width: 6.0, height: 1.0 },
  // Nailed to the tolppa, facing -x, i.e. the way the runner is coming from.
  // Wide for its height because the sign texture sizes its font off the height
  // and does not shrink to fit - see `createSign`. Smaller than it was, and
  // both dimensions scaled together so the text still fits the board: there is
  // a car parked behind the post now, and a sign long enough to overhang it
  // would be a sign drawn through a car door.
  {
    text: 'LATAUS KÄYNNISSÄ',
    position: { x: CABLE_X - 0.22, y: 0.95, z: TOLPPA_Z },
    rotationY: -Math.PI / 2,
    width: 1.3,
    height: 0.23,
    color: '#101010',
    background: '#ffd25a',
  },
  // Up on the drum's shoulder, facing the yard - so it is readable from the
  // apron, which is the only place anybody ever reads it from.
  {
    text: 'TORNIMAJOITUS',
    position: { x: TORNI.x, y: 6.0, z: TORNI.z + TORNI.radius + 0.2 },
    rotationY: 0,
    width: 6.4,
    height: 1.0,
  },
  // One over each cubicle door, facing back down the corridor.
  ...STALLS.map((spec) => ({
    text: 'WC',
    position: { x: ETEINEN.westX + 0.37, y: 2.3, z: spec.center },
    rotationY: Math.PI / 2,
    width: 0.5,
    height: 0.32,
  })),
  // The junction, in the order you meet it. There is no ETEINEN sign any more:
  // the end wall is a doorway now, and a room labels itself by what it tells
  // you is through it rather than by its own name.
  // Up, not left: the sign hangs on the leaf's own lintel, so there is nothing
  // off to the side to point at - the arrow means "through this one".
  {
    text: '↑ KEITTIÖ',
    position: { x: ETEINEN.westX + 0.37, y: 2.35, z: (RISTEYS.keittioFrom + RISTEYS.keittioTo) / 2 },
    rotationY: Math.PI / 2,
    width: 1.5,
    height: 0.4,
  },
  {
    text: 'LANIT →',
    position: { x: 131.15, y: 2.78, z: (RISTEYS.saliFrom + RISTEYS.saliTo) / 2 },
    rotationY: -Math.PI / 2,
    width: 1.9,
    height: 0.5,
  },
  // Straight down the corridor from the front door, so it is the first thing
  // you read on the way in and the thing you are still reading at the turn.
  {
    text: 'KARAOKE ↑',
    position: { x: 130.2, y: 2.78, z: ETEINEN.backZ + 0.05 },
    rotationY: 0,
    width: 2.0,
    height: 0.45,
  },
  // Over the baari, one above each tap, saying what the keg it is plumbed to has
  // in it. Left and right are read facing the counter - which is the only way
  // anybody reads them - so the left-hand tap is the one with the *larger* x.
  // Both are wide for their height because `createSign` sizes the font off the
  // height and does not shrink to fit, and sixteen characters is as much as a
  // 512-pixel canvas takes.
  {
    text: 'PANIMOSIMA 5,0 %',
    position: { x: BAARI.tapX[0] - 0.17, y: 1.72, z: BAARI.wallZ - 0.02 },
    rotationY: Math.PI,
    width: 0.8,
    height: 0.13,
    color: '#2a1e08',
    background: '#e8bf52',
  },
  {
    text: 'TÄHTI III 4,7 %',
    position: { x: BAARI.tapX[1] + 0.17, y: 1.72, z: BAARI.wallZ - 0.02 },
    rotationY: Math.PI,
    width: 0.8,
    height: 0.13,
    color: '#f1f4f9',
    background: '#16305c',
  },
  // One over each row, on the same line and at the same size, so they read
  // across the hall as a list of who is playing rather than as five notices.
  ...TABLE_CENTERS.map((cx, i) => ({
    text: TEAM_NAMES[i],
    position: { x: cx, y: TEAM_SIGN.y, z: TEAM_SIGN.z },
    rotationY: 0,
    width: TEAM_SIGN.width,
    height: TEAM_SIGN.height,
  })),
  // Over the bench in the far corner, on the wall he has his back to and at a
  // height that clears the top of his head. Printed black on white rather than
  // painted like the room's own signs: it is a sheet somebody ran off and put
  // up for the finaali, which is exactly what an official anything at a LAN in
  // a barn is.
  {
    text: 'VIRALLINEN VALVOJA',
    position: { x: VALVOJA.x, y: VALVOJA.signY, z: VALVOJA.wallZ - 0.02 },
    rotationY: Math.PI,
    width: VALVOJA.signWidth,
    height: VALVOJA.signHeight,
    color: '#101010',
    background: '#f2efe4',
  },
  // And the word over the door at the end of the same wall. Painted rather
  // than printed, unlike the notice above it: the valvoja's sheet went up for
  // this weekend, and this one names a room the building has always had. See
  // `SAUNA`.
  SAUNAN_OVI.sign,
];

/* =========================================================== projektori == */

/**
 * The clip on the valkokangas.
 *
 * Fitted to the screen's height and centred on it, a centimetre in front of
 * the sheet so the two never fight over the same pixels - which is exactly
 * what the KIMMON PAIKKA sign that used to hang here did. That sign was
 * modelled *inside* the screen's own box
 * and had never once been visible; the finish announces itself with the yellow
 * table, the yellow chair and the HUD, and did not need it.
 */
export const projection: ProjectionSpec = {
  name: 'valkokankaan-klippi',
  center: {
    x: VALKOKANGAS.x - 0.01,
    y: VALKOKANGAS.bottom + VALKOKANGAS.height / 2,
    z: (VALKOKANGAS.minZ + VALKOKANGAS.maxZ) / 2,
  },
  height: VALKOKANGAS.height,
  width: (VALKOKANGAS.height * 16) / 9,
  // Facing -x, back down the hall: the same way the screen behind it faces.
  rotationY: -Math.PI / 2,
  beam: {
    lens: { x: PROJEKTORI.x, y: PROJEKTORI.y, z: PROJEKTORI.z },
    // Lamp white, and cooler than anything else lit in the building - which is
    // what makes six metres of it read as a projector's throw rather than as
    // sun through a window.
    color: 0xdbe6ff,
    // Faint. What you are seeing is dust in a room that has been slept in, and
    // a beam you can see the edges of from the door is a searchlight.
    opacity: 0.14,
    // The same light spread over a rectangle four metres wide by the time it
    // lands, so there is much less of it per metre of air at the far end.
    falloff: 0.28,
  },
};
